import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import useTranslation from 'next-translate/useTranslation';
import Image from 'next/image';
import NextLink from 'next/link';

import { Button, Select } from '@/atoms';
import { DatePicker } from '@/molecules';

import { generateBookingURL, gtmPush } from '@/utils';

import type { FunctionComponent } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import type { TypeBookingLavandaProps } from './types';

type TypeBookingLavandaForm = {
  building: string;
  apartment: string;
  startDate: string;
  endDate: string;
};

/**
 * BookingLavanda
 */
export const BookingLavanda: FunctionComponent<TypeBookingLavandaProps> = ({
  className = '',
  apartmentsByBuilding,
  buildings,

  defaultApartment,
  defaultBuilding,
  province,
}: TypeBookingLavandaProps) => {
  const { t } = useTranslation('common');
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [hasDepartureDate, setHasDepeartureDate] = useState(
    defaultBuilding?.hasDepartureDate ?? false
  );
  const { handleSubmit, watch, register, setValue, ...methods } =
    useForm<TypeBookingLavandaForm>();
  const onSubmit: SubmitHandler<TypeBookingLavandaForm> = (data) => {
    const buildingName = buildings.find(
      (building) => building.id === parseInt(data.building)
    )?.value;

    if (data.apartment === '-1') {
      data.apartment = '';
    }
    let url = generateBookingURL({
      ...data,
      building: buildingName,
    });
    url = url.replace(/%2B/g, '+');

    gtmPush({
      event: 'availability_search',
      provincia: province?.label ?? '',
      edificio: defaultBuilding?.label ?? '',
      alojamiento: data.apartment,
      fecha: data.startDate,
    });

    window.open(url, '_blank', 'noopener noreferrer');
  };

  const buildingsSelectValues = buildings.map((building) => ({
    label: building.label,
    value: building.id,
    disabled: building.disabled,
  }));

  const mapOfAccommodation = new Map(apartmentsByBuilding);

  const watchBuilding = watch('building', defaultBuilding?.value?.toString());

  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const values = methods.getValues();
  useEffect(() => {
    setButtonDisabled(
      !values?.building || values?.building === '-1' ? true : false
    );
    let flag = false;
    buildings.forEach((build) => {
      if (`${values?.building}` === `${build.id}`) {
        flag = build.hasDepartureDate;
      }
    });

    if (flag) {
      setHasDepeartureDate(true);
    } else {
      setHasDepeartureDate(false);
    }
  }, [values]);

  const getDates = (from: Date | null) => {
    if (!from) {
      // return [new Date().now(), new Date().now()];
      return [];
    }

    const to = new Date(from).setMonth(from.getMonth() + 1);
    const cFrom = new Date(from);
    const cTo = new Date(to);

    const daysArr = [new Date(cFrom)];
    const tempDate = cFrom;

    while (tempDate < cTo) {
      tempDate.setUTCDate(tempDate.getUTCDate() + 1);
      daysArr.push(new Date(tempDate));
    }

    return daysArr;
  };
  useEffect(() => {
    setValue('building', defaultBuilding?.value?.toString() ?? '');
    if (defaultApartment) {
      setValue('apartment', defaultApartment?.value?.toString() ?? '');
    }

    setButtonDisabled(
      !values?.building || values?.building === '-1' ? true : false
    );
  }, []);
  return (
    <div
      className={`relative mt-5 rounded-md bg-surface-00 p-4 pt-8 shadow-md md:mt-7 ${className}`}
    >
      <div className="absolute right-4 top-0 -translate-y-1/2 md:right-14">
        <div className="u-aspect-square relative w-10 md:w-14">
          <Image
            src="/assets/img/logo/logo-brown.svg"
            alt="logo"
            objectFit="contain"
            layout="fill"
          />
        </div>
      </div>

      <FormProvider
        handleSubmit={handleSubmit}
        register={register}
        setValue={setValue}
        watch={watch}
        {...methods}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="mb-6 flex flex-col gap-8">
            <Select
              label={t('components.molecules.bookingLavanda.building')}
              name="building"
              elements={buildingsSelectValues}
              defaultValue={defaultBuilding}
            />
            {!hasDepartureDate && (
              <Select
                label={t('components.molecules.bookingLavanda.typeOfApartment')}
                name="apartment"
                elements={
                  mapOfAccommodation.get(parseInt(watchBuilding?.toString())) ??
                  []
                }
                {...(defaultApartment && { defaultValue: defaultApartment })}
              />
            )}
            <div className="flex flex-col">
              <label
                className="text-other--label-form uppercase"
                htmlFor="date"
              >
                {hasDepartureDate
                  ? t('components.molecules.bookingLavanda.whenDeparture')
                  : t('components.molecules.bookingLavanda.when')}
              </label>

              {/* @ts-expect-error: When hasDepartureDate selectsRange is true so onChanges is an Array */}
              <DatePicker
                minDate={new Date()}
                dateFormat="dd/MM/yyyy"
                placeholderText={
                  hasDepartureDate
                    ? t(
                        'components.molecules.bookingLavanda.whenPlaceholderDeparture'
                      )
                    : t('components.molecules.bookingLavanda.whenPlaceholder')
                }
                customInput={
                  <input
                    id="date"
                    name="date"
                    readOnly
                    className="text-body--primary w-full cursor-pointer border-b border-primary-400 py-2 font-thin text-text-primary placeholder:text-text-primary"
                  />
                }
                {...(hasDepartureDate
                  ? {
                      startDate: startDate,
                      endDate: endDate,
                      selectsRange: true,
                      excludeDates: getDates(startDate),
                      selectsDisabledDaysInRange: true,
                      onChange: (dates) => {
                        if (!Array.isArray(dates)) return;
                        setStartDate(dates[0]);
                        setValue(
                          'startDate',
                          `${dates[0]?.toLocaleDateString('sv') ?? ''}`
                        );

                        setEndDate(dates[1]);
                        setValue(
                          'endDate',
                          `${dates[1]?.toLocaleDateString('sv') ?? ''}`
                        );
                      },
                    }
                  : {
                      selected: startDate,
                      onChange: (date) => {
                        if (date !== null) {
                          setStartDate(date);
                          setValue(
                            'startDate',
                            `${date?.toLocaleDateString('sv') ?? ''}`
                          );
                          const temp = new Date(date);
                          temp.setFullYear(date.getFullYear() + 1);
                          setValue(
                            'endDate',
                            `${temp?.toLocaleDateString('sv') ?? ''}`
                          );
                          setEndDate(null);
                        }
                      },
                    })}
              />
              {hasDepartureDate && (
                <span className="text-start text-xs font-thin text-text-secondary">
                  {t('components.molecules.bookingLavanda.departureDisclaimer')}
                </span>
              )}
            </div>

            <input hidden readOnly type="date" {...register('startDate')} />

            <input hidden readOnly type="date" {...register('endDate')} />
          </div>

          <Button
            type="submit"
            variant="secondary"
            size="small"
            className="mx-auto mb-6 w-full md:w-1/2"
            disabled={buttonDisabled}
          >
            {t('components.molecules.bookingLavanda.viewApartments')}
          </Button>

          <p className="text-body--secondary text-center font-thin text-text-primary">
            {t('components.molecules.bookingLavanda.needHelp')}
            <NextLink
              href={t('components.molecules.bookingLavanda.contactLink')}
              passHref
              prefetch={false}
            >
              <a className="text-status-warning">
                {t('components.molecules.bookingLavanda.contact')}
              </a>
            </NextLink>
          </p>
        </form>
      </FormProvider>
    </div>
  );
};

BookingLavanda.displayName = 'BookingLavanda';
